<template>
  <vs-popup
    class="event-students-dialog"
    :title="title"
    :active.sync="isVisible">
    <div class="students-container">
      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <vs-progress indeterminate color="primary"></vs-progress>
        <p class="loading-text">{{ $t('common.loading') }}</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="!loading && students.length === 0" class="empty-state">
        <feather-icon icon="UsersIcon" class="empty-icon" />
        <p class="empty-text">{{ $t('lesson.no_students') }}</p>
      </div>

      <!-- Students List -->
      <div v-else class="students-list">
        <div
          v-for="(student, index) in students"
          :key="student.id || index"
          class="student-item">
          <div class="student-avatar">
            <feather-icon icon="UserIcon" class="avatar-icon" />
          </div>
          <div class="student-info">
            <span class="student-name">{{ student.name }}</span>
            <span class="student-email">{{ student.email }}</span>
          </div>
          <div v-if="student.status" class="student-status">
            <b-badge :variant="getStatusColor(student.status)" pill>
              {{ student.status.label }}
            </b-badge>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="footer-section" v-if="!loading && students.length > 0">
        <div class="student-count">
          <feather-icon icon="UsersIcon" class="count-icon" />
          <span class="count-text">
            {{ students.length }} {{ students.length === 1 ? $t('lesson.student') : $t('lesson.students') }}
          </span>
        </div>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import { BBadge } from 'bootstrap-vue'
import CalendarService from '@/services/api/CalendarService'

export default {
  name: 'EventStudentsPopup',
  components: {
    BBadge
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    eventId: {
      type: [Number, String],
      default: null
    },
    title: {
      type: String,
      default: 'Alunos'
    }
  },
  data() {
    return {
      loading: false,
      students: [],
      service: null
    }
  },
  computed: {
    isVisible: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      }
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.eventId) {
        this.loadStudents()
      }
    }
  },
  methods: {
    async loadStudents() {
      if (!this.eventId) return

      this.loading = true
      try {
        const response = await this.service.getEventStudents(this.eventId)
        console.log('📥 Resposta da API de alunos:', response)

        // A resposta pode vir como array direto ou dentro de response.data
        if (Array.isArray(response)) {
          this.students = response
        } else if (response && Array.isArray(response.data)) {
          this.students = response.data
        } else if (response && response.data) {
          this.students = [response.data]
        } else {
          this.students = []
        }

        console.log('👥 Alunos carregados:', this.students.length)
      } catch (error) {
        console.error('❌ Erro ao carregar alunos:', error)
        this.notifyError(this.$vs, this.$t('lesson.error_loading_students'))
        this.students = []
      } finally {
        this.loading = false
      }
    },
    getStatusColor(status) {
      const validVariants = ['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark']

      if (!status) return 'secondary'

      if (status.color && validVariants.includes(status.color)) {
        return status.color
      }

      const colorMap = {
        'grey': 'secondary',
        'gray': 'secondary',
        'green': 'success',
        'red': 'danger',
        'yellow': 'warning',
        'blue': 'primary',
        'orange': 'warning'
      }

      if (status.color && colorMap[status.color]) {
        return colorMap[status.color]
      }

      return 'secondary'
    }
  },
  beforeMount() {
    this.service = CalendarService.build(this.$vs)
  }
}
</script>

<style lang="scss" scoped>
.students-container {
  padding: 0.5rem 0;
  min-height: 200px;
  max-height: 500px;
  overflow-y: auto;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  gap: 1rem;

  .loading-text {
    color: #6b7280;
    font-size: 0.875rem;
    margin: 0;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  gap: 1rem;

  .empty-icon {
    width: 48px;
    height: 48px;
    color: #d1d5db;
  }

  .empty-text {
    color: #6b7280;
    font-size: 0.95rem;
    margin: 0;
  }
}

.students-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.5rem 0;
}

.student-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #fafafa;
  border-radius: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }
}

.student-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  min-height: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  transition: all 0.2s ease;

  .avatar-icon {
    width: 20px;
    height: 20px;
    color: white;
  }
}

.student-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-width: 0;
}

.student-name {
  font-size: 0.95rem;
  color: #374151;
  font-weight: 600;
  word-break: break-word;
}

.student-email {
  font-size: 0.85rem;
  color: #6b7280;
  word-break: break-word;
}

.student-status {
  display: flex;
  align-items: center;
}

.footer-section {
  display: flex;
  justify-content: center;
  padding-top: 1rem;
  margin-top: 1rem;
  border-top: 1px solid #f1f1f1;
}

.student-count {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;

  .count-icon {
    width: 18px;
    height: 18px;
  }
}

// Dark mode support
.theme-dark {
  .students-container {
    background: transparent;
  }

  .student-item {
    background: rgba(255, 255, 255, 0.05);

    &:hover {
      background: rgba(255, 255, 255, 0.08);
    }
  }

  .student-name {
    color: #e5e7eb;
  }

  .student-email {
    color: #9ca3af;
  }

  .loading-text,
  .empty-text {
    color: #9ca3af;
  }

  .empty-icon {
    color: #4b5563;
  }

  .footer-section {
    border-top-color: rgba(255, 255, 255, 0.1);
  }

  .student-count {
    color: #9ca3af;
  }
}

// Responsive adjustments
@media (max-width: 640px) {
  .student-item {
    padding: 0.875rem;
  }

  .student-avatar {
    min-width: 36px;
    min-height: 36px;

    .avatar-icon {
      width: 18px;
      height: 18px;
    }
  }

  .student-name {
    font-size: 0.875rem;
  }

  .student-email {
    font-size: 0.8rem;
  }
}
</style>

